import React, { useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import { Field } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { AdminPagesHeader } from '@atlassian/jira-admin-pages-common/src/ui/admin-pages-header/index.tsx';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracking-provider/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { ErrorPage as ErrorBoundaryPage } from '@atlassian/jira-error-boundary/src/ui/error-page';
import { useFlagsService } from '@atlassian/jira-flags';
import type { DatePickerValue } from '@atlassian/jira-fuzzy-date-picker/src/types.tsx';
import { FuzzyDatePicker } from '@atlassian/jira-fuzzy-date-picker/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	SCREEN,
	ContextualAnalyticsData,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { JIRA_PORTFOLIO } from '@atlassian/jira-shared-types/src/application.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { saveFiscalMonth } from '../services';
import { getMonthDates, getMonthFromDate } from '../utils';
import { messages } from './messages';
import { Preview } from './preview';

const SUCCESS_FLAG_ID = 'financialYearDatesSuccessFlag';
const ERROR_FLAG_ID = 'financialYearDatesErrorFlag';

export const FinancialYearDates = ({ startMonth }: { startMonth: number }) => {
	const { formatMessage } = useIntl();
	const { software: edition } = useAppEditions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isEditing, setIsEditing] = useState(false);
	const [fyMonth, setFYMonth] = useState<number>(startMonth);
	const { showFlag, dismissFlag } = useFlagsService();

	const resetValue = useCallback(
		async (previousValue: number) => {
			dismissFlag(SUCCESS_FLAG_ID);
			setFYMonth(previousValue);
			await saveFiscalMonth({ month: previousValue });
		},
		[dismissFlag],
	);

	const onChange = useCallback(
		async ({ start }: DatePickerValue | undefined = { start: '????-01-01', end: '' }) => {
			setIsEditing(false);
			// The date that fuzzy date picker returns is in local time.
			// It's easier to manually parse the string than fiddle around with native JS Date API.
			const localMonth = getMonthFromDate(start);
			const previousFYMonth = fyMonth; // save previous value in case user wants to undo
			if (previousFYMonth === localMonth) return;
			try {
				setFYMonth(localMonth);
				await saveFiscalMonth({ month: localMonth });
				dismissFlag(SUCCESS_FLAG_ID);
				dismissFlag(ERROR_FLAG_ID);
				showFlag({
					type: 'success',
					title: formatMessage(messages.successFlagTitle),
					description: formatMessage(messages.successFlagDescription),
					isAutoDismiss: true,
					id: SUCCESS_FLAG_ID,
					actions: [
						{
							content: messages.resetDatesLabel,
							onClick: () => resetValue(previousFYMonth),
						},
					],
				});
				fireTrackAnalytics(createAnalyticsEvent({}), 'financialYear updated', {
					fiscalMonth: localMonth,
				});
			} catch (_) {
				setFYMonth(previousFYMonth);
				showFlag({
					type: 'error',
					title: formatMessage(messages.errorFlagTitle),
					isAutoDismiss: true,
					id: ERROR_FLAG_ID,
				});
			}
		},
		[createAnalyticsEvent, dismissFlag, formatMessage, fyMonth, resetValue, showFlag],
	);

	return (
		<UFOSegment name="view-advanced-roadmaps-financial-year-dates">
			<ViewExperienceTrackingProvider
				experience="viewPortfolio3FinancialYearDates"
				analyticsSource="portfolio3"
				application={JIRA_PORTFOLIO}
				edition={edition}
			>
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="financialYearDates">
					<JSErrorBoundary
						id="advanced-roadmaps.financial-year-dates"
						packageName="jiraPortfolio3FinancialYearDates"
						teamName="chronos"
						fallback={({ error }: { error: Error }) => <ErrorBoundaryPage error={error} />}
						withExperienceTracker
					>
						<FireScreenAnalytics />
						<MarkProductStart />
						<SubProductUpdater subProduct="software" />
						<AdminPagesHeader title={formatMessage(messages.pageHeading)} />
						<ContentWrapper>
							<Field name="fiscal-year-month" label={formatMessage(messages.monthSelectLabel)}>
								{() => (
									<FuzzyDatePicker
										value={getMonthDates(fyMonth)}
										withTimeScale={false}
										onChange={onChange}
										isOpen={isEditing}
										localStorageKey="advanced-roadmaps"
										onClick={() => setIsEditing(true)}
										onClose={() => setIsEditing(false)}
									/>
								)}
							</Field>
							<Box xcss={previewWrapperStyles}>
								<Preview fyStartMonth={fyMonth} />
							</Box>
							<Box xcss={sectionMessageWrapperStyles}>
								<SectionMessage appearance="information">
									{formatMessage(messages.sectionMessage)}
								</SectionMessage>
							</Box>
						</ContentWrapper>
					</JSErrorBoundary>
				</ContextualAnalyticsData>
			</ViewExperienceTrackingProvider>
		</UFOSegment>
	);
};

const sectionMessageWrapperStyles = xcss({
	marginTop: 'space.300',
});

const previewWrapperStyles = xcss({
	marginTop: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	width: '400px',
});
